import { useEffect, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loading } from '@/kit';
import { useResumeSession, useWizardSession } from '@/domains/sales/wizard/api/useWizard';
import { hasAccountOpeningMark } from '@/domains/sales/wizard/accountOpening';
import { hasProvenPhone, stepAfterOtp } from '@/domains/sales/wizard/resume';
import { slugForStep } from '@/domains/sales/wizard/steps';
import type { WizardSession, WizardStepSlug } from '@/domains/sales/wizard/types';

// Entrada por marcador, historial, botón atrás y «Reabrir wizard» de la bandeja de Prospectos.
export const Route = createFileRoute('/wizard/s/$sessionId/')({
    component: ResumeRedirect,
});

/**
 * Entrar a una venta viva con progreso probado es retomarla, y retomarla cuesta un OTP nuevo. Manda
 * el teléfono probado, no el cursor: `currentStep` es el último COMPLETADO, y el 01 ya es progreso.
 */
function needsResumeOtp(session: WizardSession): boolean {
    return session.state === 'ACTIVE' && (session.currentStep !== '01' || hasProvenPhone(session));
}

/**
 * Adónde entra la venta que no pasa por el candado. `currentStep` es el último paso COMPLETADO: en
 * una venta que ya no está viva miente, y ahí manda el destino que calcula el silo.
 */
function entryStep(session: WizardSession): WizardStepSlug {
    // La venta viva que llega aquí es la del 01 sin teléfono probado, y el destino del silo apunta
    // a S02: aplicarlo saltaría el paso que le falta.
    if (session.state === 'ACTIVE') return slugForStep(session.currentStep, session);
    // La apertura siempre resuelve con el criterio del silo: el cursor nunca la manda a un paso de venta.
    if (hasAccountOpeningMark(session)) return stepAfterOtp(session);
    // Sin destino del silo no hay nada mejor que el cursor: el default de S02 sacaría del camino a
    // la venta derivada, que ya pasó por el KYC.
    return session.resumeSlug || session.resumeTarget
        ? stepAfterOtp(session)
        : slugForStep(session.currentStep, session);
}

function ResumeRedirect() {
    const { sessionId } = Route.useParams();
    const navigate = useNavigate();
    const { data: session } = useWizardSession(sessionId);
    const resumeSession = useResumeSession();
    const redirected = useRef(false);

    useEffect(() => {
        if (!session || redirected.current) return;
        redirected.current = true;

        const go = (step: WizardStepSlug): Promise<void> =>
            navigate({
                to: '/wizard/s/$sessionId/$step',
                params: { sessionId, step },
                replace: true,
            });

        void (async () => {
            if (session.resumeOtpRequired) return go('otp');
            // La venta pausada no se reanuda: el silo la rechaza por no estar ACTIVE.
            if (!needsResumeOtp(session)) return go(entryStep(session));
            // Un fallo de la reanudación no habilita el atajo: el destino sigue siendo el paso 01.
            await resumeSession.mutateAsync(sessionId).catch(() => undefined);
            return go('otp');
        })();
    }, [session, sessionId, navigate, resumeSession]);

    return <Loading size="lg" />;
}
